import { useEffect, useMemo, useState } from "react"
import type { FormEvent, ReactNode } from "react"
import { inputCls, textareaCls, labelCls, Toggle } from "./FormComponents"
import { ContentAssetPicker, type ContentAssetOption } from "./ContentAssetPicker"
import { VideoAssociationPicker, type PendingVideo } from "./VideoAssociationPicker"

type LessonType = 'youtube' | 'private_video' | 'reading' | 'assignment' | 'document'

type LessonEditorValue = {
  title: string
  description: string
  youtube_url: string
  duration_minutes: number
  order_index: number
  is_preview: boolean
  is_private_video: boolean
}

type LessonEditorProps = {
  formId: string
  initialValue: LessonEditorValue
  lessonType?: LessonType
  error?: string
  onSubmit: (value: LessonEditorValue) => void | Promise<void>
  /** Fires on every field edit — lets the parent drive a live preview/checklist. */
  onChange?: (value: LessonEditorValue) => void
  /** Only set when editing an existing lesson. New lessons can still get a pending video/document — it's applied atomically on the first save. */
  lessonId?: string | null

  // Video (private_video) — picked here but only associated when the lesson is saved
  // (video-manager-lesson-association-improvements, decisiones 5-7).
  pendingVideo?: PendingVideo
  onChangeVideo?: (video: PendingVideo) => void
  /** Set after a 409 on submit — the picked video was taken by another lesson meanwhile. */
  videoFieldError?: string

  // Document (PDF library) — associate an existing PDF (deferred, same as video), or
  // upload a new one (still immediate — an upload always needs a real target to land on).
  documentAssets?: ContentAssetOption[]
  pendingDocumentId?: string | null
  loadingDocumentAssets?: boolean
  onChangeDocumentId?: (documentId: string | null) => void
  documentFieldError?: string
  uploadingNewDocument?: boolean
  onUploadNewDocument?: (file: File) => void | Promise<void>
}

const YOUTUBE_ID_REGEX =
  /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/

function extractYoutubeId(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ""
  return trimmed.match(YOUTUBE_ID_REGEX)?.[1] ?? ""
}

function IconFileText() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 2.5h5.5L12 5v8.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-10a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M5.5 8h5M5.5 10.3h5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  )
}

function IconUploadCloud() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 13V5m0 0-3 3m3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 14.5v1.5a1.5 1.5 0 0 0 1.5 1.5h8a1.5 1.5 0 0 0 1.5-1.5v-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

/** Consistent card wrapper for the content-type panel, matching the "Detalles" card below it. */
function ContentCard({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
      {children}
    </div>
  )
}

export default function LessonEditor({
  formId,
  initialValue,
  lessonType = 'youtube',
  error = "",
  onSubmit,
  onChange,
  lessonId = null,
  pendingVideo = null,
  onChangeVideo,
  videoFieldError,
  documentAssets = [],
  pendingDocumentId = null,
  loadingDocumentAssets = false,
  onChangeDocumentId,
  documentFieldError,
  uploadingNewDocument = false,
  onUploadNewDocument,
}: LessonEditorProps) {
  const [form, setForm] = useState<LessonEditorValue>(initialValue)

  useEffect(() => { setForm(initialValue) }, [initialValue])
  useEffect(() => { onChange?.(form) }, [form]) // eslint-disable-line react-hooks/exhaustive-deps

  const youtubeVideoId = useMemo(() => extractYoutubeId(form.youtube_url), [form.youtube_url])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    await onSubmit({
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
      youtube_url: form.youtube_url.trim(),
    })
  }

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit}>
      {/* Contenido — un panel consistente por tipo, siempre en el mismo lugar */}
      {lessonType === 'youtube' && (
        <ContentCard>
          <label className={labelCls}>URL de YouTube</label>
          <input
            type="url"
            value={form.youtube_url}
            onChange={(e) => setForm((p) => ({ ...p, youtube_url: e.target.value }))}
            placeholder="https://www.youtube.com/watch?v=..."
            className={inputCls}
          />
          {youtubeVideoId ? (
            <p className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5.5" stroke="currentColor"/>
                <path d="M3.5 6l2 2 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Video detectado: <code className="font-mono">{youtubeVideoId}</code>
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Pega el enlace del video y el ID se detectará automáticamente.</p>
          )}
        </ContentCard>
      )}

      {lessonType === 'private_video' && (
        <ContentCard>
          <VideoAssociationPicker
            lessonTitle={form.title}
            currentLessonId={lessonId}
            value={pendingVideo}
            onChange={(v) => onChangeVideo?.(v)}
            fieldError={videoFieldError}
          />
          <Toggle
            checked={form.is_private_video}
            onChange={(v) => setForm((p) => ({ ...p, is_private_video: v }))}
            label="Video privado"
            description="Requiere autenticación para reproducirse"
          />
        </ContentCard>
      )}

      {lessonType === 'document' && (
        <ContentCard>
          <ContentAssetPicker
            label="Documento"
            icon={<IconFileText />}
            items={documentAssets}
            selectedId={pendingDocumentId}
            loading={loadingDocumentAssets}
            onSelect={(id) => onChangeDocumentId?.(id || null)}
            emptyMessage="No hay PDFs en la biblioteca de este curso todavía."
          />
          {documentFieldError && (
            <p className="text-xs font-medium text-error-500">{documentFieldError}</p>
          )}

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <span className="text-[11px] text-gray-500 dark:text-gray-400">o</span>
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>

          {!lessonId ? (
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Subir un PDF nuevo estará disponible después de guardar la lección por primera vez — mientras tanto puedes asociar uno ya existente arriba.
            </p>
          ) : (
            <label className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 px-4 py-5 text-center dark:border-gray-600 ${uploadingNewDocument ? "opacity-60" : "hover:border-brand-300 dark:hover:border-brand-500/50"}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <IconUploadCloud />
              </span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {uploadingNewDocument ? "Subiendo y asociando..." : "Arrastra un PDF aquí o haz clic para subir"}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Se asocia automáticamente a esta lección al terminar</span>
              <input
                type="file"
                accept="application/pdf"
                disabled={uploadingNewDocument}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void onUploadNewDocument?.(file)
                }}
                className="sr-only"
              />
            </label>
          )}
        </ContentCard>
      )}

      {(lessonType === 'reading' || lessonType === 'assignment') && (
        <ContentCard>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {lessonType === 'reading'
              ? "Esta lección es texto libre — no necesita contenido asociado. Escribe el material de lectura en el resumen de abajo."
              : "Esta lección es una entrega evaluable manualmente — no necesita contenido asociado."}
          </p>
        </ContentCard>
      )}

      {/* Detalles de la lección */}
      <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <p className="text-sm font-semibold text-gray-800 dark:text-white/90">Detalles de la lección</p>

        <div>
          <label className={labelCls}>Título</label>
          <input
            required
            autoFocus
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder="Ej. Introducción al diseño orbital"
            className={inputCls}
          />
        </div>

        <div>
          <label className={labelCls}>
            Resumen de la lección / notas de apoyo <span className="font-normal text-gray-400">(opcional)</span>
          </label>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Qué va a aprender el estudiante y cualquier nota de apoyo para seguir la lección"
            className={textareaCls}
          />
        </div>
      </div>

      {error ? (
        <p role="alert" aria-live="assertive" className="text-sm font-medium text-error-500">
          {error}
        </p>
      ) : null}
    </form>
  )
}
